import React, { useState, useEffect, ReactNode, Children, isValidElement, useRef, startTransition } from 'react'
import classes from './styles.module.scss'
import classNames from 'classnames'

interface ISimpleTabsProps {
  children: ReactNode
  fullWidth?: boolean
  showTabs?: boolean
  tabContainerStyle?: React.CSSProperties
  isSticky?: boolean
  activeTabId?: string
  onTabChange?: (tabId: string) => void
}

interface ITabProps {
  label: string
  tabId: string | number // Ensure tabId is required
  isActive?: boolean
  onClick?: (tabId: string) => void
  tabStyle?: React.CSSProperties
}

interface IContentProps {
  tabId: string | number
  children: ReactNode
  tabContentStyle?: React.CSSProperties
  isActive?: boolean
}

const SimpleTabs: React.FC<ISimpleTabsProps> & {
  Tab: React.FC<ITabProps>
  Content: React.FC<IContentProps>
} = (props) => {
  const {
    children,
    fullWidth = false,
    showTabs = true,
    tabContainerStyle = {},
    isSticky = false,
    activeTabId,
    onTabChange,
  } = props
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const hasSetInitialTab = useRef(false)

  /**
   * Effect hook that sets the initial active tab to the first available tab if it hasn't been set already.
   * Iterates over the children to find the first child that is a valid SimpleTabs.Tab element.
   * Once found, it sets this tab's ID as the active tab and marks that the initial tab has been set.
   */
  useEffect(() => {
    if (activeTabId || hasSetInitialTab.current) return

    const firstTab = Children.toArray(children).find((child) => {
      return isValidElement<ITabProps>(child) && child.type === SimpleTabs.Tab
    }) as React.ReactElement<ITabProps> | undefined

    if (firstTab) {
      startTransition(() => setActiveTab(firstTab.props.tabId.toString()))
      hasSetInitialTab.current = true
    }
  }, [children, activeTabId])

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId)
    onTabChange?.(tabId)
  }

  const currentActiveTab = activeTabId ?? activeTab

  return (
    <div className={classes.simpleTabs}>
      <div style={{ ...tabContainerStyle }}>
        {showTabs &&
          <div
            className={classNames({
              [classes.tabs]: true,
              [classes.fullWidth]: fullWidth,
              [classes.sticky]: isSticky,
            })}
          >
            {Children.map(children, (child) => {
              if (isValidElement<ITabProps>(child) && child.type === SimpleTabs.Tab) {
                return React.cloneElement(child, {
                  isActive: child.props.tabId.toString() === currentActiveTab,
                  onClick: handleTabClick,
                })
              }
              return null
            })}
          </div>
        }
      </div>
      <div className={classes.content}>
        {Children.map(children, (child) => {
          if (isValidElement<IContentProps>(child) && child.type === SimpleTabs.Content) {
            const isActive = child.props.tabId.toString() === currentActiveTab
            return React.cloneElement(child, { isActive })
          }
          return null
        })}
      </div>
    </div >
  )
}

SimpleTabs.Tab = Object.assign(({ label, tabId, isActive, onClick, tabStyle }: ITabProps) => {
  return (
    <button
      className={`${classes.tab} ${isActive ? classes.active : ''}`}
      onClick={() => onClick?.(tabId.toString())}
      style={{ ...tabStyle }}
    >
      {label}
    </button>
  )
}, { displayName: 'SimpleTabs.Tab' })

SimpleTabs.Content = Object.assign(({ children, isActive, tabContentStyle }: IContentProps & { isActive?: boolean }) => {
  return (
    <div
      className={`${classes.tabContent} ${isActive ? classes.activeContent : ''}`}
      style={{ ...tabContentStyle }}
    >
      {children}
    </div>
  )
}, { displayName: 'SimpleTabs.Content' })


export { SimpleTabs }